import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Pause, Play, RotateCcw, Timer } from 'lucide-react';
import { formatMmSs } from './cookingModeUtils.js';

export interface CountdownTimerProps {
  durationSeconds: number;
  label: string;
}

type Phase = 'idle' | 'running' | 'paused' | 'finished';

interface AudioContextCtor {
  new (): AudioContextLike;
}

interface AudioContextLike {
  currentTime: number;
  destination: unknown;
  createOscillator(): OscillatorNodeLike;
  createGain(): GainNodeLike;
  close(): Promise<void>;
}

interface OscillatorNodeLike {
  frequency: { value: number };
  type: 'sine' | 'square' | 'sawtooth' | 'triangle' | 'custom';
  connect(dest: unknown): void;
  start(when?: number): void;
  stop(when?: number): void;
}

interface GainNodeLike {
  gain: { value: number; setValueAtTime(value: number, when: number): void; linearRampToValueAtTime(value: number, when: number): void };
  connect(dest: unknown): void;
}

declare global {
  interface Window {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  }
}

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  return window.AudioContext ?? window.webkitAudioContext ?? null;
}

function playChime(): void {
  const Ctor = getAudioContextCtor();
  if (!Ctor) return;
  try {
    const ctx = new Ctor();
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.25, now + 0.02);
    gain.gain.linearRampToValueAtTime(0.0001, now + 0.5);
    gain.connect(ctx.destination);
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 880;
    osc1.connect(gain);
    osc1.start(now);
    osc1.stop(now + 0.25);
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 660;
    osc2.connect(gain);
    osc2.start(now + 0.25);
    osc2.stop(now + 0.5);
  } catch {
    /* swallow — chime is best-effort */
  }
}

export function CountdownTimer({
  durationSeconds,
  label,
}: CountdownTimerProps): ReactElement {
  const [phase, setPhase] = useState<Phase>('idle');
  const [remainingSeconds, setRemainingSeconds] = useState<number>(durationSeconds);
  const endsAtRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTick = useCallback((): void => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearTick();
  }, [clearTick]);

  useEffect(() => {
    setPhase('idle');
    setRemainingSeconds(durationSeconds);
    endsAtRef.current = null;
    clearTick();
  }, [durationSeconds, clearTick]);

  const tick = useCallback((): void => {
    const endsAt = endsAtRef.current;
    if (endsAt === null) return;
    const remainingMs = Math.max(0, endsAt - Date.now());
    const nextSeconds = Math.ceil(remainingMs / 1000);
    setRemainingSeconds(nextSeconds);
    if (remainingMs <= 0) {
      clearTick();
      endsAtRef.current = null;
      setPhase('finished');
      playChime();
    }
  }, [clearTick]);

  const start = useCallback((): void => {
    if (phase === 'finished') {
      endsAtRef.current = Date.now() + durationSeconds * 1000;
      setRemainingSeconds(durationSeconds);
      setPhase('running');
      intervalRef.current = setInterval(tick, 250);
      return;
    }
    if (phase === 'paused' && endsAtRef.current !== null) {
      const remaining = endsAtRef.current - Date.now();
      endsAtRef.current = Date.now() + remaining;
    } else {
      endsAtRef.current = Date.now() + durationSeconds * 1000;
      setRemainingSeconds(durationSeconds);
    }
    setPhase('running');
    clearTick();
    intervalRef.current = setInterval(tick, 250);
  }, [phase, durationSeconds, tick, clearTick]);

  const pause = useCallback((): void => {
    if (phase !== 'running') return;
    if (endsAtRef.current !== null) {
      const remaining = Math.max(0, endsAtRef.current - Date.now());
      setRemainingSeconds(Math.ceil(remaining / 1000));
    }
    setPhase('paused');
    clearTick();
  }, [phase, clearTick]);

  const reset = useCallback((): void => {
    clearTick();
    endsAtRef.current = null;
    setPhase('idle');
    setRemainingSeconds(durationSeconds);
  }, [durationSeconds, clearTick]);

  if (phase === 'idle') {
    return (
      <button
        type="button"
        onClick={start}
        className="flex min-h-[48px] items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium"
        style={{
          borderColor: 'var(--accent)',
          color: 'var(--accent)',
          background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
        }}
        data-testid="cooking-timer-start"
        data-label={label}
      >
        <Timer size={16} aria-hidden="true" />
        Start {label} <span aria-hidden="true">▶</span>
      </button>
    );
  }

  if (phase === 'finished') {
    return (
      <button
        type="button"
        onClick={reset}
        className="flex min-h-[48px] items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium"
        style={{
          borderColor: 'var(--accent)',
          color: 'var(--accent-contrast)',
          background: 'var(--accent)',
          animation: 'pulse 1s ease-in-out 2',
        }}
        data-testid="cooking-timer-finished"
        data-flashing="true"
        aria-label="Timer finished, tap to dismiss"
      >
        <RotateCcw size={16} aria-hidden="true" />
        Done — tap to dismiss
      </button>
    );
  }

  const isPaused = phase === 'paused';
  return (
    <div className="flex items-center gap-2" data-testid="cooking-timer-running">
      <button
        type="button"
        onClick={isPaused ? start : pause}
        className="flex min-h-[48px] items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium"
        style={{
          borderColor: 'var(--border)',
          color: 'var(--text-main)',
          background: 'transparent',
        }}
        data-testid="cooking-timer-toggle"
        aria-label={isPaused ? 'Resume timer' : 'Pause timer'}
      >
        {isPaused ? <Play size={16} aria-hidden="true" /> : <Pause size={16} aria-hidden="true" />}
        <span data-testid="cooking-timer-display">{formatMmSs(remainingSeconds)}</span>
      </button>
      <button
        type="button"
        onClick={reset}
        className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-md border"
        style={{
          borderColor: 'var(--border)',
          color: 'var(--text-muted)',
          background: 'transparent',
        }}
        data-testid="cooking-timer-reset"
        aria-label="Reset timer"
      >
        <RotateCcw size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
